import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  where,
} from "firebase/firestore";
import { db } from "../../firebase";
import { isLocationEdgeType } from "./locationTypes";

export const DEFAULT_GRAPH_ID = "default";

const META_IMPORTS_DOC = "imports";
const PLACE_TYPE_LABEL = "Place";
const PROPERTY_KIND = "Property";
const TYPE_KIND = "Type";

const BASE_SCHEMA_TYPES = ["Object", "Animal", "Mammal", "Person"];
const BASE_SCHEMA_PROPERTIES = [
  {
    label: "Mass (kg)",
    key: "massKg",
    valueType: "Number",
    unit: "kg",
    cardinality: "0..1",
    description: "Body mass in kilograms.",
    privacyDefault: "private",
  },
  {
    label: "Warm Blooded",
    key: "warmBlooded",
    valueType: "Boolean",
    unit: null,
    cardinality: "0..1",
    description: "Whether the organism is warm-blooded.",
    privacyDefault: "public",
  },
  {
    label: "Live Birth",
    key: "liveBirth",
    valueType: "Boolean",
    unit: null,
    cardinality: "0..1",
    description: "Whether offspring are delivered live.",
    privacyDefault: "public",
  },
  {
    label: "Birthdate",
    key: "birthdate",
    valueType: "Date",
    unit: null,
    cardinality: "0..1",
    description: "Date-only birth date (YYYY-MM-DD).",
    privacyDefault: "sensitive",
  },
  {
    label: "Email",
    key: "email",
    valueType: "Email",
    unit: null,
    cardinality: "0..1",
    description: "Email address.",
    privacyDefault: "sensitive",
  },
  {
    label: "Phone",
    key: "phone",
    valueType: "PhoneE164",
    unit: null,
    cardinality: "0..1",
    description: "Phone in E.164 format (+15551234567).",
    privacyDefault: "sensitive",
  },
];

export function graphNodesCol(graphId = DEFAULT_GRAPH_ID) {
  return collection(db, "graphs", graphId, "nodes");
}

export function graphEdgesCol(graphId = DEFAULT_GRAPH_ID) {
  return collection(db, "graphs", graphId, "edges");
}

export function graphNodeDoc(graphId = DEFAULT_GRAPH_ID, nodeId) {
  return doc(db, "graphs", graphId, "nodes", nodeId);
}

export function graphEdgeDoc(graphId = DEFAULT_GRAPH_ID, edgeId) {
  return doc(db, "graphs", graphId, "edges", edgeId);
}

export function graphImportsDoc(graphId = DEFAULT_GRAPH_ID) {
  return doc(db, "graphs", graphId, "meta", META_IMPORTS_DOC);
}

function normalizePropertyNodeProps(rawProps = {}) {
  return {
    kind: PROPERTY_KIND,
    key: String(rawProps.key || "").trim(),
    valueType: String(rawProps.valueType || "String"),
    unit: rawProps.unit == null || rawProps.unit === "" ? null : String(rawProps.unit),
    cardinality: String(rawProps.cardinality || "0..1"),
    description: rawProps.description == null ? "" : String(rawProps.description),
    constraints: rawProps.constraints && typeof rawProps.constraints === "object" ? rawProps.constraints : {},
    privacyDefault: rawProps.privacyDefault || "private",
  };
}

function normalizeNodeInput(payload = {}) {
  const normalized = {
    label: typeof payload.label === "string" ? payload.label : "",
    props: payload.props && typeof payload.props === "object" ? payload.props : {},
    ownerUid: payload.ownerUid || "",
    ...extractTemporalFields(payload),
  };

  if (payload.ui && typeof payload.ui === "object") {
    normalized.ui = {
      x: Number(payload.ui.x) || 0,
      y: Number(payload.ui.y) || 0,
    };
  }

  return normalized;
}

function normalizeEdgeInput(payload = {}) {
  return {
    type: typeof payload.type === "string" ? payload.type : "",
    from: {
      nodeId: payload?.from?.nodeId || "",
      ...(payload?.from?.portId ? { portId: payload.from.portId } : {}),
    },
    to: {
      nodeId: payload?.to?.nodeId || "",
      ...(payload?.to?.portId ? { portId: payload.to.portId } : {}),
    },
    props: payload.props && typeof payload.props === "object" ? payload.props : {},
    ownerUid: payload.ownerUid || "",
    ...extractTemporalFields(payload),
  };
}

function normalizeTemporalValue(value) {
  if (value == null) return null;
  if (typeof value?.toDate === "function") return value;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === "number" || typeof value === "string") {
    const next = new Date(value);
    return Number.isNaN(next.getTime()) ? null : next;
  }
  return null;
}

function extractTemporalFields(payload = {}) {
  return {
    occurredAt: normalizeTemporalValue(payload.occurredAt),
    validFrom: normalizeTemporalValue(payload.validFrom),
    validTo: normalizeTemporalValue(payload.validTo),
  };
}

function normalizeLocationEdgeProps(props = {}) {
  const next = {};
  if (typeof props.validFrom === "string") next.validFrom = props.validFrom;
  if (typeof props.validTo === "string") next.validTo = props.validTo;
  if (typeof props.notes === "string") next.notes = props.notes;
  return next;
}

function normalizePlacePayload(placeResult = {}) {
  const name = String(placeResult.name || "").trim();
  const formattedAddress = String(placeResult.formattedAddress || "").trim();
  const placeId = String(placeResult.placeId || placeResult.place_id || "").trim();
  const lat = Number(placeResult.lat);
  const lng = Number(placeResult.lng);
  return {
    label: name || formattedAddress || "Place",
    props: {
      kind: "Place",
      placeId,
      name,
      formattedAddress,
      ...(Number.isFinite(lat) ? { lat } : {}),
      ...(Number.isFinite(lng) ? { lng } : {}),
      ...(Array.isArray(placeResult.googleTypes) && placeResult.googleTypes.length ? { googleTypes: placeResult.googleTypes } : {}),
    },
  };
}

async function loadOwnerGraph(graphId = DEFAULT_GRAPH_ID, ownerUid) {
  const [nodesSnap, edgesSnap] = await Promise.all([
    getDocs(query(graphNodesCol(graphId), where("ownerUid", "==", ownerUid), limit(1200))),
    getDocs(query(graphEdgesCol(graphId), where("ownerUid", "==", ownerUid), limit(2200))),
  ]);
  return {
    nodes: nodesSnap.docs.map((item) => ({ id: item.id, ...item.data() })),
    edges: edgesSnap.docs.map((item) => ({ id: item.id, ...item.data() })),
  };
}

export async function createNode(graphId = DEFAULT_GRAPH_ID, payload) {
  const next = normalizeNodeInput(payload);
  return addDoc(graphNodesCol(graphId), {
    ...next,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function upsertNode(graphId = DEFAULT_GRAPH_ID, nodeId, payload) {
  const next = normalizeNodeInput(payload);
  await setDoc(graphNodeDoc(graphId, nodeId), {
    ...next,
    updatedAt: serverTimestamp(),
    createdAt: serverTimestamp(),
  }, { merge: true });
}

export async function updateNode(graphId = DEFAULT_GRAPH_ID, nodeId, patch = {}) {
  await setDoc(graphNodeDoc(graphId, nodeId), {
    ...patch,
    updatedAt: serverTimestamp(),
  }, { merge: true });
}

export async function deleteNode(graphId = DEFAULT_GRAPH_ID, nodeId, ownerUid) {
  const fromQuery = ownerUid
    ? query(
      graphEdgesCol(graphId),
      where("ownerUid", "==", ownerUid),
      where("from.nodeId", "==", nodeId)
    )
    : query(
      graphEdgesCol(graphId),
      where("from.nodeId", "==", nodeId)
    );
  const toQuery = ownerUid
    ? query(
      graphEdgesCol(graphId),
      where("ownerUid", "==", ownerUid),
      where("to.nodeId", "==", nodeId)
    )
    : query(
      graphEdgesCol(graphId),
      where("to.nodeId", "==", nodeId)
    );
  const edgeSnap = await getDocs(fromQuery);
  const edgeSnapIn = await getDocs(toQuery);
  const deletions = [];
  edgeSnap.forEach((row) => deletions.push(deleteDoc(row.ref)));
  edgeSnapIn.forEach((row) => deletions.push(deleteDoc(row.ref)));
  await Promise.all(deletions);
  await deleteDoc(graphNodeDoc(graphId, nodeId));
}

export async function createEdge(graphId = DEFAULT_GRAPH_ID, payload) {
  const next = normalizeEdgeInput(payload);
  return addDoc(graphEdgesCol(graphId), {
    ...next,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function upsertEdge(graphId = DEFAULT_GRAPH_ID, edgeId, payload) {
  const next = normalizeEdgeInput(payload);
  await setDoc(graphEdgeDoc(graphId, edgeId), {
    ...next,
    updatedAt: serverTimestamp(),
    createdAt: serverTimestamp(),
  }, { merge: true });
}

export async function updateEdge(graphId = DEFAULT_GRAPH_ID, edgeId, patch = {}) {
  await setDoc(graphEdgeDoc(graphId, edgeId), {
    ...patch,
    updatedAt: serverTimestamp(),
  }, { merge: true });
}

export async function deleteEdge(graphId = DEFAULT_GRAPH_ID, edgeId) {
  await deleteDoc(graphEdgeDoc(graphId, edgeId));
}

export function listenNodes(graphId = DEFAULT_GRAPH_ID, ownerUid, onNext, onError) {
  const q = query(
    graphNodesCol(graphId),
    where("ownerUid", "==", ownerUid),
    orderBy("updatedAt", "desc")
  );
  return onSnapshot(q, (snap) => {
    onNext(snap.docs.map((item) => ({ id: item.id, ...item.data() })));
  }, onError);
}

export function listenEdges(graphId = DEFAULT_GRAPH_ID, ownerUid, onNext, onError) {
  const q = query(
    graphEdgesCol(graphId),
    where("ownerUid", "==", ownerUid),
    orderBy("updatedAt", "desc")
  );
  return onSnapshot(q, (snap) => {
    onNext(snap.docs.map((item) => ({ id: item.id, ...item.data() })));
  }, onError);
}

export async function searchNodesByLabel(graphId = DEFAULT_GRAPH_ID, ownerUid, term = "") {
  const q = query(
    graphNodesCol(graphId),
    where("ownerUid", "==", ownerUid),
    limit(250)
  );
  const snap = await getDocs(q);
  const normalized = term.trim().toLowerCase();
  return snap.docs
    .map((item) => ({ id: item.id, ...item.data() }))
    .filter((node) => {
      if (!normalized) return true;
      return (node.label || "").toLowerCase().includes(normalized);
    })
    .sort((a, b) => (a.label || "").localeCompare(b.label || ""));
}

function buildAdjacency(edges, edgeTypeFilter) {
  const adjacency = new Map();
  edges.forEach((edge) => {
    if (edgeTypeFilter && edge.type !== edgeTypeFilter) return;
    if (!adjacency.has(edge.from.nodeId)) adjacency.set(edge.from.nodeId, []);
    if (!adjacency.has(edge.to.nodeId)) adjacency.set(edge.to.nodeId, []);
    adjacency.get(edge.from.nodeId).push(edge.to.nodeId);
    adjacency.get(edge.to.nodeId).push(edge.from.nodeId);
  });
  return adjacency;
}

export async function getNeighborhood(graphId = DEFAULT_GRAPH_ID, ownerUid, rootNodeId, depth = 1, edgeTypeFilter) {
  const [nodesSnap, edgesSnap] = await Promise.all([
    getDocs(query(graphNodesCol(graphId), where("ownerUid", "==", ownerUid))),
    getDocs(query(graphEdgesCol(graphId), where("ownerUid", "==", ownerUid))),
  ]);
  const nodes = nodesSnap.docs.map((item) => ({ id: item.id, ...item.data() }));
  const edges = edgesSnap.docs.map((item) => ({ id: item.id, ...item.data() }));
  const adjacency = buildAdjacency(edges, edgeTypeFilter);
  const seen = new Set([rootNodeId]);
  let frontier = [rootNodeId];

  for (let level = 0; level < depth; level += 1) {
    const next = [];
    frontier.forEach((nodeId) => {
      (adjacency.get(nodeId) || []).forEach((neighborId) => {
        if (!seen.has(neighborId)) {
          seen.add(neighborId);
          next.push(neighborId);
        }
      });
    });
    frontier = next;
    if (!frontier.length) break;
  }

  const nodeSet = new Set(seen);
  const matchedNodes = nodes.filter((node) => nodeSet.has(node.id));
  const matchedEdges = edges.filter((edge) => nodeSet.has(edge.from.nodeId) && nodeSet.has(edge.to.nodeId));
  return { nodes: matchedNodes, edges: matchedEdges };
}

async function getTypeAncestry(graphId, ownerUid) {
  const [nodesSnap, edgesSnap] = await Promise.all([
    getDocs(query(graphNodesCol(graphId), where("ownerUid", "==", ownerUid))),
    getDocs(query(graphEdgesCol(graphId), where("ownerUid", "==", ownerUid))),
  ]);

  const nodes = nodesSnap.docs.map((item) => ({ id: item.id, ...item.data() }));
  const edges = edgesSnap.docs.map((item) => ({ id: item.id, ...item.data() }));
  const typeIds = new Set(nodes.filter((node) => node.props?.kind === "Type").map((node) => node.id));

  const parentMap = new Map();
  edges.forEach((edge) => {
    if (edge.type !== "IS_A") return;
    if (!typeIds.has(edge.from.nodeId) || !typeIds.has(edge.to.nodeId)) return;
    if (!parentMap.has(edge.from.nodeId)) parentMap.set(edge.from.nodeId, new Set());
    parentMap.get(edge.from.nodeId).add(edge.to.nodeId);
  });

  return { nodes, edges, typeIds, parentMap };
}

export async function getTypesForNode(graphId = DEFAULT_GRAPH_ID, ownerUid, nodeId) {
  const { nodes, edges, typeIds, parentMap } = await getTypeAncestry(graphId, ownerUid);
  const hasTypeEdges = edges.filter((edge) => edge.type === "HAS_TYPE" && edge.from.nodeId === nodeId);

  const inferred = new Set();
  const queue = [];

  hasTypeEdges.forEach((edge) => {
    if (typeIds.has(edge.to.nodeId)) {
      inferred.add(edge.to.nodeId);
      queue.push(edge.to.nodeId);
    }
  });

  while (queue.length) {
    const current = queue.shift();
    (parentMap.get(current) || new Set()).forEach((parentId) => {
      if (!inferred.has(parentId)) {
        inferred.add(parentId);
        queue.push(parentId);
      }
    });
  }

  const byId = new Map(nodes.map((node) => [node.id, node]));
  return [...inferred].map((typeId) => byId.get(typeId)).filter(Boolean);
}

export async function getNodesByType(graphId = DEFAULT_GRAPH_ID, ownerUid, typeNodeId) {
  const [nodesSnap, edgesSnap] = await Promise.all([
    getDocs(query(graphNodesCol(graphId), where("ownerUid", "==", ownerUid))),
    getDocs(query(graphEdgesCol(graphId), where("ownerUid", "==", ownerUid))),
  ]);
  const nodes = nodesSnap.docs.map((item) => ({ id: item.id, ...item.data() }));
  const edges = edgesSnap.docs.map((item) => ({ id: item.id, ...item.data() }));

  const childMap = new Map();
  edges.forEach((edge) => {
    if (edge.type !== "IS_A") return;
    if (!childMap.has(edge.to.nodeId)) childMap.set(edge.to.nodeId, new Set());
    childMap.get(edge.to.nodeId).add(edge.from.nodeId);
  });

  const descendants = new Set([typeNodeId]);
  const queue = [typeNodeId];
  while (queue.length) {
    const current = queue.shift();
    const children = childMap.get(current) || new Set();
    children.forEach((childId) => {
      if (!descendants.has(childId)) {
        descendants.add(childId);
        queue.push(childId);
      }
    });
  }

  const nodeIds = new Set(
    edges
      .filter((edge) => edge.type === "HAS_TYPE" && descendants.has(edge.to.nodeId))
      .map((edge) => edge.from.nodeId)
  );

  return nodes.filter((node) => nodeIds.has(node.id));
}

export async function getImportsMeta(graphId = DEFAULT_GRAPH_ID) {
  const snap = await getDoc(graphImportsDoc(graphId));
  if (!snap.exists()) {
    return {
      milfordImported: false,
      toloImported: false,
      baseTypesCreated: false,
    };
  }
  return {
    milfordImported: !!snap.data().milfordImported,
    toloImported: !!snap.data().toloImported,
    baseTypesCreated: !!snap.data().baseTypesCreated,
  };
}

export async function updateImportsMeta(graphId = DEFAULT_GRAPH_ID, patch = {}) {
  await setDoc(graphImportsDoc(graphId), {
    ...patch,
    updatedAt: serverTimestamp(),
  }, { merge: true });
}

export async function findPlaceNodeByPlaceId(graphId = DEFAULT_GRAPH_ID, ownerUid, placeId) {
  const normalizedPlaceId = String(placeId || "").trim();
  if (!ownerUid || !normalizedPlaceId) return null;
  const q = query(
    graphNodesCol(graphId),
    where("ownerUid", "==", ownerUid),
    where("props.placeId", "==", normalizedPlaceId),
    limit(1)
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const row = snap.docs[0];
  return { id: row.id, ...row.data() };
}

export async function ensurePlaceTypeNode(graphId = DEFAULT_GRAPH_ID, ownerUid) {
  if (!ownerUid) throw new Error("ownerUid is required");
  const q = query(graphNodesCol(graphId), where("ownerUid", "==", ownerUid), limit(500));
  const snap = await getDocs(q);
  const found = snap.docs
    .map((item) => ({ id: item.id, ...item.data() }))
    .find((node) => node.props?.kind === "Type" && (node.label || "").toLowerCase() === PLACE_TYPE_LABEL.toLowerCase());
  if (found) return found;

  const payload = {
    label: PLACE_TYPE_LABEL,
    props: { kind: "Type" },
    ownerUid,
  };
  const ref = await createNode(graphId, payload);
  return { id: ref.id, ...payload };
}

export async function createPlaceNodeFromPlaceResult(graphId = DEFAULT_GRAPH_ID, ownerUid, placeResult = {}) {
  if (!ownerUid) throw new Error("ownerUid is required");
  const normalized = normalizePlacePayload(placeResult);
  if (!normalized.props.placeId) throw new Error("placeId is required");
  const payload = {
    ...normalized,
    ownerUid,
  };
  const ref = await createNode(graphId, payload);
  return { id: ref.id, ...payload };
}

export async function addHasTypeEdge(graphId = DEFAULT_GRAPH_ID, ownerUid, subjectId, typeNodeId) {
  if (!ownerUid || !subjectId || !typeNodeId) return null;
  const q = query(
    graphEdgesCol(graphId),
    where("ownerUid", "==", ownerUid),
    where("from.nodeId", "==", subjectId),
    limit(250)
  );
  const snap = await getDocs(q);
  const existing = snap.docs
    .map((item) => ({ id: item.id, ...item.data() }))
    .find((edge) => edge.type === "HAS_TYPE" && edge.to?.nodeId === typeNodeId);
  if (existing) return existing;

  const ref = await createEdge(graphId, {
    type: "HAS_TYPE",
    from: { nodeId: subjectId },
    to: { nodeId: typeNodeId },
    props: {},
    ownerUid,
  });
  return {
    id: ref.id,
    type: "HAS_TYPE",
    from: { nodeId: subjectId },
    to: { nodeId: typeNodeId },
    props: {},
    ownerUid,
  };
}

export async function addLocationEdge(
  graphId = DEFAULT_GRAPH_ID,
  ownerUid,
  subjectId,
  placeNodeId,
  edgeType,
  edgeProps = {}
) {
  if (!ownerUid || !subjectId || !placeNodeId) throw new Error("ownerUid, subjectId, and placeNodeId are required");
  if (!isLocationEdgeType(edgeType)) throw new Error(`Invalid location edge type: ${edgeType}`);
  const payload = {
    type: edgeType,
    from: { nodeId: subjectId },
    to: { nodeId: placeNodeId },
    props: normalizeLocationEdgeProps(edgeProps),
    ownerUid,
  };
  const ref = await createEdge(graphId, payload);
  return { id: ref.id, ...payload };
}

export async function listLocationEdgesForSubject(graphId = DEFAULT_GRAPH_ID, ownerUid, subjectId) {
  if (!ownerUid || !subjectId) return [];
  const q = query(
    graphEdgesCol(graphId),
    where("ownerUid", "==", ownerUid),
    where("from.nodeId", "==", subjectId),
    limit(500)
  );
  const snap = await getDocs(q);
  return snap.docs
    .map((item) => ({ id: item.id, ...item.data() }))
    .filter((edge) => isLocationEdgeType(edge.type));
}

export async function listIncomingLocationEdgesForPlace(graphId = DEFAULT_GRAPH_ID, ownerUid, placeNodeId) {
  if (!ownerUid || !placeNodeId) return [];
  const q = query(
    graphEdgesCol(graphId),
    where("ownerUid", "==", ownerUid),
    where("to.nodeId", "==", placeNodeId),
    limit(500)
  );
  const snap = await getDocs(q);
  return snap.docs
    .map((item) => ({ id: item.id, ...item.data() }))
    .filter((edge) => isLocationEdgeType(edge.type));
}

function findTypeNodeByLabel(nodes, label) {
  const target = String(label || "").toLowerCase();
  return nodes.find((node) => node.props?.kind === TYPE_KIND && (node.label || "").toLowerCase() === target);
}

function findPropertyNodeByKey(nodes, key) {
  const target = String(key || "").toLowerCase();
  return nodes.find((node) => node.props?.kind === PROPERTY_KIND && (node.props?.key || "").toLowerCase() === target);
}

export async function ensureBaseSchema(graphId = DEFAULT_GRAPH_ID, ownerUid) {
  if (!ownerUid) throw new Error("ownerUid is required");

  const created = { types: [], properties: [], edges: [] };
  const loaded = await loadOwnerGraph(graphId, ownerUid);
  const nodes = [...loaded.nodes];

  for (const label of BASE_SCHEMA_TYPES) {
    let node = findTypeNodeByLabel(nodes, label);
    if (!node) {
      const payload = { label, props: { kind: TYPE_KIND }, ownerUid };
      const ref = await createNode(graphId, payload);
      node = { id: ref.id, ...payload };
      nodes.push(node);
      created.types.push(node.id);
    }
  }

  for (const def of BASE_SCHEMA_PROPERTIES) {
    let node = findPropertyNodeByKey(nodes, def.key);
    if (!node) {
      const payload = {
        label: def.label,
        props: normalizePropertyNodeProps(def),
        ownerUid,
      };
      const ref = await createNode(graphId, payload);
      node = { id: ref.id, ...payload };
      nodes.push(node);
      created.properties.push(node.id);
    }
  }

  const typeByLabel = new Map(BASE_SCHEMA_TYPES.map((label) => [label, findTypeNodeByLabel(nodes, label)]));
  const propByKey = new Map(BASE_SCHEMA_PROPERTIES.map((def) => [def.key, findPropertyNodeByKey(nodes, def.key)]));
  const { edges } = await loadOwnerGraph(graphId, ownerUid);
  const edgeSet = new Set(edges.map((edge) => `${edge.type}|${edge.from?.nodeId}|${edge.to?.nodeId}`));

  const wantedEdges = [
    { type: "IS_A", from: { nodeId: typeByLabel.get("Mammal").id }, to: { nodeId: typeByLabel.get("Animal").id }, props: {} },
    { type: "IS_A", from: { nodeId: typeByLabel.get("Animal").id }, to: { nodeId: typeByLabel.get("Object").id }, props: {} },
    { type: "IS_A", from: { nodeId: typeByLabel.get("Person").id }, to: { nodeId: typeByLabel.get("Mammal").id }, props: {} },
    {
      type: "HAS_PROPERTY",
      from: { nodeId: typeByLabel.get("Object").id },
      to: { nodeId: propByKey.get("massKg").id },
      props: { required: false },
    },
    {
      type: "HAS_PROPERTY",
      from: { nodeId: typeByLabel.get("Mammal").id },
      to: { nodeId: propByKey.get("warmBlooded").id },
      props: { required: false },
    },
    {
      type: "HAS_PROPERTY",
      from: { nodeId: typeByLabel.get("Mammal").id },
      to: { nodeId: propByKey.get("liveBirth").id },
      props: { required: false },
    },
    {
      type: "HAS_PROPERTY",
      from: { nodeId: typeByLabel.get("Person").id },
      to: { nodeId: propByKey.get("birthdate").id },
      props: { required: false, privacyDefault: "sensitive" },
    },
    {
      type: "HAS_PROPERTY",
      from: { nodeId: typeByLabel.get("Person").id },
      to: { nodeId: propByKey.get("email").id },
      props: { required: false, privacyDefault: "sensitive" },
    },
    {
      type: "HAS_PROPERTY",
      from: { nodeId: typeByLabel.get("Person").id },
      to: { nodeId: propByKey.get("phone").id },
      props: { required: false, privacyDefault: "sensitive" },
    },
  ];

  for (const edge of wantedEdges) {
    const key = `${edge.type}|${edge.from.nodeId}|${edge.to.nodeId}`;
    if (edgeSet.has(key)) continue;
    const ref = await createEdge(graphId, { ...edge, ownerUid });
    edgeSet.add(key);
    created.edges.push(ref.id);
  }

  return {
    created,
    typeIds: Object.fromEntries([...typeByLabel.entries()].map(([label, node]) => [label, node.id])),
    propertyIds: Object.fromEntries([...propByKey.entries()].map(([key, node]) => [key, node.id])),
  };
}

export async function getDirectTypes(graphId = DEFAULT_GRAPH_ID, ownerUid, entityNodeId) {
  if (!ownerUid || !entityNodeId) return [];
  const { edges } = await loadOwnerGraph(graphId, ownerUid);
  return edges
    .filter((edge) => edge.type === "HAS_TYPE" && edge.from?.nodeId === entityNodeId)
    .map((edge) => edge.to.nodeId)
    .filter(Boolean);
}

export async function getTypeClosure(graphId = DEFAULT_GRAPH_ID, ownerUid, typeNodeIds = []) {
  if (!ownerUid) return [];
  const { edges } = await loadOwnerGraph(graphId, ownerUid);
  const parentMap = new Map();
  edges.forEach((edge) => {
    if (edge.type !== "IS_A") return;
    if (!parentMap.has(edge.from?.nodeId)) parentMap.set(edge.from?.nodeId, new Set());
    parentMap.get(edge.from?.nodeId).add(edge.to?.nodeId);
  });

  const seen = new Set(typeNodeIds.filter(Boolean));
  const queue = [...seen];
  while (queue.length) {
    const current = queue.shift();
    (parentMap.get(current) || new Set()).forEach((parentId) => {
      if (!parentId || seen.has(parentId)) return;
      seen.add(parentId);
      queue.push(parentId);
    });
  }
  return [...seen];
}

export async function getInheritedPropertyDefs(graphId = DEFAULT_GRAPH_ID, ownerUid, entityNodeId) {
  if (!ownerUid || !entityNodeId) return [];
  const { nodes, edges } = await loadOwnerGraph(graphId, ownerUid);
  const directTypes = edges
    .filter((edge) => edge.type === "HAS_TYPE" && edge.from?.nodeId === entityNodeId)
    .map((edge) => edge.to?.nodeId)
    .filter(Boolean);

  const closure = await getTypeClosure(graphId, ownerUid, directTypes);
  const propertyById = new Map(nodes.filter((node) => node.props?.kind === PROPERTY_KIND).map((node) => [node.id, node]));

  const byKey = new Map();
  edges.forEach((edge) => {
    if (edge.type !== "HAS_PROPERTY") return;
    if (!closure.includes(edge.from?.nodeId)) return;
    const propDef = propertyById.get(edge.to?.nodeId);
    if (!propDef) return;
    const key = propDef.props?.key || propDef.id;
    if (!byKey.has(key)) {
      byKey.set(key, {
        propertyDefId: propDef.id,
        propertyDef: propDef,
        providedByTypes: new Set(),
        required: false,
        defaultValue: null,
        privacyDefault: propDef.props?.privacyDefault || "private",
      });
    }
    const slot = byKey.get(key);
    slot.providedByTypes.add(edge.from.nodeId);
    if (edge.props?.required === true) slot.required = true;
    if (slot.defaultValue == null && edge.props && Object.prototype.hasOwnProperty.call(edge.props, "defaultValue")) {
      slot.defaultValue = edge.props.defaultValue;
    }
    if (edge.props?.privacyDefault) slot.privacyDefault = edge.props.privacyDefault;
  });

  return [...byKey.values()]
    .map((slot) => ({
      ...slot,
      providedByTypes: [...slot.providedByTypes],
    }))
    .sort((a, b) => (a.propertyDef.label || "").localeCompare(b.propertyDef.label || ""));
}

export async function getValuesForEntity(graphId = DEFAULT_GRAPH_ID, ownerUid, entityNodeId) {
  if (!ownerUid || !entityNodeId) return {};
  const q = query(
    graphEdgesCol(graphId),
    where("ownerUid", "==", ownerUid),
    where("from.nodeId", "==", entityNodeId),
    limit(800)
  );
  const snap = await getDocs(q);
  const map = {};
  snap.docs
    .map((item) => ({ id: item.id, ...item.data() }))
    .filter((edge) => edge.type === "HAS_VALUE")
    .forEach((edge) => {
      const propertyDefId = edge.to?.nodeId;
      if (!propertyDefId) return;
      map[propertyDefId] = {
        edgeId: edge.id,
        ...(edge.props || {}),
      };
    });
  return map;
}

function isValidCalendarDateString(value) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [y, m, d] = value.split("-").map((part) => Number(part));
  const date = new Date(Date.UTC(y, m - 1, d));
  return date.getUTCFullYear() === y && date.getUTCMonth() === m - 1 && date.getUTCDate() === d;
}

export function normalizeValue(valueType, rawInput) {
  const type = String(valueType || "String");
  if (rawInput == null) return null;

  if (type === "Boolean") {
    if (typeof rawInput === "boolean") return rawInput;
    if (typeof rawInput === "string") {
      if (rawInput.toLowerCase() === "true") return true;
      if (rawInput.toLowerCase() === "false") return false;
    }
    return null;
  }

  if (type === "Number") {
    const n = Number(rawInput);
    return Number.isFinite(n) ? n : null;
  }

  const value = String(rawInput).trim();
  if (!value) return null;
  return value;
}

export function validateValue(valueType, value, constraints = {}) {
  if (value == null || value === "") return { ok: true, error: "" };
  const type = String(valueType || "String");

  if (type === "Email") {
    const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value));
    return ok ? { ok: true, error: "" } : { ok: false, error: "Invalid email format." };
  }

  if (type === "PhoneE164") {
    const ok = /^\+[1-9]\d{7,15}$/.test(String(value));
    return ok ? { ok: true, error: "" } : { ok: false, error: "Phone must be E.164 (+ and 8-16 digits)." };
  }

  if (type === "Date") {
    const ok = isValidCalendarDateString(String(value));
    return ok ? { ok: true, error: "" } : { ok: false, error: "Date must be a real YYYY-MM-DD value." };
  }

  if (type === "Number") {
    if (typeof value !== "number" || !Number.isFinite(value)) {
      return { ok: false, error: "Value must be a number." };
    }
    if (constraints?.min != null && value < Number(constraints.min)) {
      return { ok: false, error: `Must be >= ${constraints.min}.` };
    }
    if (constraints?.max != null && value > Number(constraints.max)) {
      return { ok: false, error: `Must be <= ${constraints.max}.` };
    }
  }

  if (constraints?.enum && Array.isArray(constraints.enum) && constraints.enum.length) {
    if (!constraints.enum.map((item) => String(item)).includes(String(value))) {
      return { ok: false, error: "Value must be one of the allowed enum values." };
    }
  }

  if (constraints?.pattern) {
    try {
      const re = new RegExp(String(constraints.pattern));
      if (!re.test(String(value))) {
        return { ok: false, error: "Value does not match required pattern." };
      }
    } catch (_error) {
      return { ok: false, error: "Invalid schema regex pattern." };
    }
  }

  if (type === "Boolean" && typeof value !== "boolean") {
    return { ok: false, error: "Value must be true or false." };
  }

  return { ok: true, error: "" };
}

export async function upsertValueEdge(
  graphId = DEFAULT_GRAPH_ID,
  ownerUid,
  entityNodeId,
  propertyDefId,
  value,
  optionalPrivacy
) {
  if (!ownerUid || !entityNodeId || !propertyDefId) throw new Error("ownerUid, entityNodeId, and propertyDefId are required");

  const { nodes, edges } = await loadOwnerGraph(graphId, ownerUid);
  const propDef = nodes.find((node) => node.id === propertyDefId && node.props?.kind === PROPERTY_KIND);
  if (!propDef) throw new Error("Property definition not found.");

  const inherited = await getInheritedPropertyDefs(graphId, ownerUid, entityNodeId);
  const slot = inherited.find((item) => item.propertyDefId === propertyDefId);
  const resolvedPrivacy = optionalPrivacy || slot?.privacyDefault || propDef.props?.privacyDefault || "private";

  const normalized = normalizeValue(propDef.props?.valueType, value);
  const validation = validateValue(propDef.props?.valueType, normalized, propDef.props?.constraints || {});
  if (!validation.ok) throw new Error(validation.error);

  const payloadProps = {
    value: normalized,
    valueType: propDef.props?.valueType || "String",
    unit: propDef.props?.unit || null,
    privacy: resolvedPrivacy,
    updatedAt: serverTimestamp(),
  };

  const existing = edges.find((edge) => (
    edge.type === "HAS_VALUE"
    && edge.from?.nodeId === entityNodeId
    && edge.to?.nodeId === propertyDefId
  ));

  if (existing) {
    await updateEdge(graphId, existing.id, { props: payloadProps });
    return existing.id;
  }

  const ref = await createEdge(graphId, {
    type: "HAS_VALUE",
    from: { nodeId: entityNodeId },
    to: { nodeId: propertyDefId },
    props: payloadProps,
    ownerUid,
  });
  return ref.id;
}

export async function removeValueEdge(graphId = DEFAULT_GRAPH_ID, ownerUid, entityNodeId, propertyDefId) {
  if (!ownerUid || !entityNodeId || !propertyDefId) return;
  const q = query(
    graphEdgesCol(graphId),
    where("ownerUid", "==", ownerUid),
    where("from.nodeId", "==", entityNodeId),
    limit(500)
  );
  const snap = await getDocs(q);
  const existing = snap.docs
    .map((item) => ({ id: item.id, ...item.data() }))
    .find((edge) => edge.type === "HAS_VALUE" && edge.to?.nodeId === propertyDefId);
  if (!existing) return;
  await deleteEdge(graphId, existing.id);
}

export async function searchTypesByLabel(graphId = DEFAULT_GRAPH_ID, ownerUid, term = "") {
  const rows = await searchNodesByLabel(graphId, ownerUid, term);
  return rows.filter((node) => node.props?.kind === TYPE_KIND);
}

export async function searchPropertyDefs(graphId = DEFAULT_GRAPH_ID, ownerUid, term = "") {
  const rows = await searchNodesByLabel(graphId, ownerUid, term);
  const lower = term.trim().toLowerCase();
  return rows.filter((node) => {
    if (node.props?.kind !== PROPERTY_KIND) return false;
    if (!lower) return true;
    const key = String(node.props?.key || "").toLowerCase();
    return key.includes(lower) || (node.label || "").toLowerCase().includes(lower);
  });
}
